import React, { useEffect, useState } from "react";
import {
  Shield,
  Users,
  Crown,
  UserCheck,
  Settings,
  Plus,
  Pencil,
  Ban,
  Trash2,
  MoreHorizontal,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { ApiError, rolesApi } from "../utils/api";
import { toast } from "sonner";

interface Role {
  _id: string;
  name: string;
  description?: string;
  permissions: string[];
  isActive: boolean;
  createdAt?: string;
}

// Core roles baked into the platform's auth logic - the backend refuses to
// disable/edit/delete these, so the UI hides those actions too rather than
// letting an admin click something that always 403s.
const PROTECTED_ROLES = ["ATHLETE", "COACH", "AGENT", "ADMIN"];

const emptyForm = {
  name: "",
  description: "",
  permissionsText: "",
};

export function Roles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await rolesApi.getAll();

      if (response.success) {
        setRoles(response.data.roles || []);
      } else {
        throw new Error(response.message || "Failed to load roles");
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load roles";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const openAdd = () => {
    setEditItem(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditItem(role);
    setForm({
      name: role.name,
      description: role.description || "",
      permissionsText: (role.permissions || []).join("\n"),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim().toUpperCase().replace(/\s+/g, "_"),
        description: form.description.trim(),
        permissions: form.permissionsText
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
      };

      const response = editItem
        ? await rolesApi.update(editItem._id, payload)
        : await rolesApi.create(payload);

      if (response.success) {
        toast.success(editItem ? "Role updated" : "Role created");
        setDialogOpen(false);
        loadRoles();
      } else {
        toast.error(response.message || "Save failed");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (role: Role) => {
    try {
      const response = await rolesApi.toggleStatus(role._id);
      toast[response.success ? "success" : "error"](
        response.message || (role.isActive ? "Role disabled" : "Role enabled")
      );
      if (response.success) loadRoles();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update role status");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await rolesApi.delete(deleteTarget._id);
      toast[response.success ? "success" : "error"](
        response.message || "Role deleted permanently"
      );
      if (response.success) {
        setDeleteTarget(null);
        loadRoles();
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete role");
    }
  };

  const getRoleIcon = (roleName: string) => {
    switch (roleName) {
      case "ADMIN":
        return <Shield className="h-5 w-5 text-red-500" />;
      case "COACH":
        return <UserCheck className="h-5 w-5 text-blue-500" />;
      case "ATHLETE":
        return <Users className="h-5 w-5 text-green-500" />;
      case "AGENT":
        return <Settings className="h-5 w-5 text-purple-500" />;
      case "MODERATOR":
        return <Crown className="h-5 w-5 text-orange-500" />;
      default:
        return <Users className="h-5 w-5 text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case "ADMIN":
        return "bg-red-100 text-red-800 border-red-200";
      case "COACH":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "ATHLETE":
        return "bg-green-100 text-green-800 border-green-200";
      case "AGENT":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "MODERATOR":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1>System Roles</h1>
            <Badge variant="secondary" className="rounded-full">
              {roles.length}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            View and manage roles and permissions in the Coachable system.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Role
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => {
          const isProtected = PROTECTED_ROLES.includes(role.name);

          return (
            <Card key={role._id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getRoleIcon(role.name)}
                    <CardTitle className="text-lg">{role.name}</CardTitle>
                  </div>

                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className={getRoleBadgeColor(role.name)}>
                      {role.isActive ? "Active" : "Disabled"}
                    </Badge>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(role)} disabled={isProtected}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => handleToggleStatus(role)}
                          disabled={isProtected}
                        >
                          <Ban className="h-4 w-4 mr-2" />
                          {role.isActive ? "Disable" : "Enable"}
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteTarget(role)}
                          disabled={isProtected}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <CardDescription className="text-sm">
                  {role.description || "No description"}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm mb-2 text-muted-foreground">Permissions:</p>
                    <div className="flex flex-wrap gap-1">
                      {(role.permissions || []).slice(0, 3).map((permission) => (
                        <Badge key={permission} variant="outline" className="text-xs">
                          {permission.replace(/_/g, " ").toLowerCase()}
                        </Badge>
                      ))}
                      {role.permissions && role.permissions.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{role.permissions.length - 3} more
                        </Badge>
                      )}
                      {(!role.permissions || role.permissions.length === 0) && (
                        <span className="text-xs text-muted-foreground">None configured</span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Created:{" "}
                    {role.createdAt ? new Date(role.createdAt).toLocaleDateString() : "N/A"}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Role" : "Add Role"}</DialogTitle>
            <DialogDescription>
              {editItem
                ? "Update this role's description and permissions."
                : "Create a new custom role. Core roles (Athlete, Coach, Agent, Admin) can't be renamed or removed."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="e.g. MODERATOR"
                disabled={!!editItem}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="role-description">Description</Label>
              <Textarea
                id="role-description"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({ ...current, description: event.target.value }))
                }
                placeholder="What can this role do?"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="role-permissions">Permissions (one per line)</Label>
              <Textarea
                id="role-permissions"
                value={form.permissionsText}
                onChange={(event) =>
                  setForm((current) => ({ ...current, permissionsText: event.target.value }))
                }
                placeholder={"view_profile\nmanage_clients"}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editItem ? "Save Changes" : "Create Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget?.name}</strong>? This cannot be undone.
              Roles currently assigned to users can't be deleted - disable it
              instead if it's still in use.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
